import { Fragment } from 'react';
import { CheckCircle2, Circle, Loader2 } from 'lucide-react';

// ==================== Types ====================
type StepStatus = 'completed' | 'current' | 'pending';

interface Step {
    id: string;
    label: string;
    description?: string;
}

interface ProgressTimelineProps {
    steps?: Step[];
    currentStepId: string;
    className?: string;
    variant?: 'horizontal' | 'vertical';
}

// ==================== Default Steps ====================
const defaultSteps: Step[] = [
    {
        id: 'review',
        label: 'Revisão',
        description: 'Leia o contrato',
    },
    {
        id: 'consent',
        label: 'Aceite',
        description: 'Aceite os termos',
    },
    {
        id: 'signature',
        label: 'Assinatura',
        description: 'Assine o contrato',
    },
    {
        id: 'confirmation',
        label: 'Confirmação',
        description: 'Contrato finalizado',
    },
];

// ==================== Component ====================
export const ProgressTimeline: React.FC<ProgressTimelineProps> = ({
    steps = defaultSteps,
    currentStepId,
    className = '',
    variant = 'horizontal',
}) => {
    const currentIndex = steps.findIndex((step) => step.id === currentStepId);

    const getStepStatus = (index: number): StepStatus => {
        if (index < currentIndex) return 'completed';
        if (index === currentIndex) return 'current';
        return 'pending';
    };

    const renderStepIcon = (status: StepStatus) => {
        switch (status) {
            case 'completed':
                return (
                    <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center">
                        <CheckCircle2 className="w-5 h-5 text-white" />
                    </div>
                );
            case 'current':
                return (
                    <div className="w-8 h-8 rounded-full bg-sky-500 flex items-center justify-center animate-pulse">
                        <Loader2 className="w-5 h-5 text-white animate-spin" />
                    </div>
                );
            case 'pending':
            default:
                return (
                    <div className="w-8 h-8 rounded-full bg-white/10 border-2 border-white/30 flex items-center justify-center">
                        <Circle className="w-4 h-4 text-white/30" />
                    </div>
                );
        }
    };

    // ==================== Horizontal Variant ====================
    if (variant === 'horizontal') {
        return (
            <div className={`${className}`}>
                <div className="flex items-center justify-between">
                    {steps.map((step, index) => {
                        const status = getStepStatus(index);
                        const isLast = index === steps.length - 1;

                        return (
                            <Fragment key={step.id}>
                                {/* Step */}
                                <div className="flex flex-col items-center">
                                    {renderStepIcon(status)}
                                    <div className="mt-2 text-center">
                                        <p
                                            className={`text-sm font-medium ${status === 'completed'
                                                ? 'text-emerald-400'
                                                : status === 'current'
                                                    ? 'text-sky-400'
                                                    : 'text-white/40'
                                                }`}
                                        >
                                            {step.label}
                                        </p>
                                        {step.description && (
                                            <p className="text-xs text-white/30 mt-0.5 hidden sm:block">
                                                {step.description}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Connector */}
                                {!isLast && (
                                    <div className="flex-1 mx-2 sm:mx-4">
                                        <div
                                            className={`h-0.5 transition-all ${status === 'completed' || getStepStatus(index + 1) !== 'pending'
                                                ? 'bg-emerald-500'
                                                : 'bg-white/10'
                                                }`}
                                        />
                                    </div>
                                )}
                            </Fragment>
                        );
                    })}
                </div>

                {/* Progress Bar */}
                <div className="mt-6">
                    <div className="flex items-center justify-between text-xs text-white/50 mb-1">
                        <span>Progresso</span>
                        <span>
                            {Math.round(((currentIndex + 1) / steps.length) * 100)}%
                        </span>
                    </div>
                    <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-sky-500 to-emerald-500 rounded-full transition-all duration-500"
                            style={{
                                width: `${((currentIndex + 1) / steps.length) * 100}%`,
                            }}
                        />
                    </div>
                </div>
            </div>
        );
    }

    // ==================== Vertical Variant ====================
    return (
        <div className={`${className}`}>
            <div className="space-y-0">
                {steps.map((step, index) => {
                    const status = getStepStatus(index);
                    const isLast = index === steps.length - 1;

                    return (
                        <div key={step.id} className="flex">
                            {/* Icon & Line */}
                            <div className="flex flex-col items-center">
                                {renderStepIcon(status)}
                                {!isLast && (
                                    <div
                                        className={`w-0.5 h-12 transition-all ${status === 'completed' ? 'bg-emerald-500' : 'bg-white/10'
                                            }`}
                                    />
                                )}
                            </div>

                            {/* Content */}
                            <div className="ml-4 pb-8">
                                <p
                                    className={`font-medium ${status === 'completed'
                                        ? 'text-emerald-400'
                                        : status === 'current'
                                            ? 'text-sky-400'
                                            : 'text-white/40'
                                        }`}
                                >
                                    {step.label}
                                </p>
                                {step.description && (
                                    <p className="text-sm text-white/50 mt-0.5">
                                        {step.description}
                                    </p>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ProgressTimeline;
